/**
 * D-EZ-95 — THE HISTORY LAYER'S ONE DOOR IN.
 *
 * Every history function reads `HistorySeason[]`: the archived seasons of a
 * league, oldest first, plus — optionally — the season being raced right now.
 * A settled `CareerSeason` (lib/careerRunner.ts) already IS a `HistorySeason`,
 * structurally; a season in progress goes through `liveHistorySeason`.
 *
 * Nothing here is stored. Every record, award and milestone is a fold over the
 * same race results the standings were folded from, so the history can never
 * disagree with the record book (TELEMETRY_SPEC: "derive, never duplicate").
 */

import type { DriverCard } from '../types.ts';
import type { SeasonResult, SeasonRaceResult, SeasonState } from '../seasonPlay.ts';
import type { SeasonStandings } from '../standings.ts';
import type { MoneyEvent } from '../finance/money.ts';
import type { RoyaltyBreakdown } from '../finance/seasonFinance.ts';
import type { CareerCarry } from '../careerCarry.ts';

/** The parts of a settled or live season the history reads. */
export interface HistorySeason {
  seasonNumber: number;
  season: Pick<SeasonResult, 'seasonId' | 'championId' | 'finalStandings' | 'races' | 'feudThreads'> & {
    pointsStandings?: SeasonStandings;
    playoff?: SeasonResult['playoff'];
    fatalities?: readonly { driverId: string; name: string; raceNumber: number }[];
    rookies?: readonly { driverId: string }[];
  };
  /** The cards the season was raced with. */
  roster: readonly DriverCard[];
  teams: readonly { teamId: string; name: string; driverIds: readonly string[] }[];
  finance?: { events: readonly MoneyEvent[]; royalties?: readonly RoyaltyBreakdown[] };
  /** Present once the winter has been settled. */
  offseason?: {
    roster: readonly DriverCard[];
    demotions: readonly { driverId: string; reason: string }[];
    retirements?: readonly { driverId: string; age: number }[];
    carry?: CareerCarry;
  };
  /** False while the season is still being raced. Default true. */
  complete?: boolean;
}

/**
 * A season being raced, as a `HistorySeason`. Its standings are the points
 * table after the last race run; there is no champion and no winter yet.
 */
export function liveHistorySeason(live: {
  seasonNumber: number;
  roster: readonly DriverCard[];
  teams: readonly { teamId: string; name: string; driverIds: readonly string[] }[];
  state: SeasonState;
}): HistorySeason {
  const { state } = live;
  const standings = state.standingsAfterRace[state.standingsAfterRace.length - 1] ?? EMPTY_STANDINGS;
  return {
    seasonNumber: live.seasonNumber,
    season: {
      seasonId: state.seasonId,
      championId: null,
      finalStandings: standings,
      pointsStandings: standings,
      races: state.races,
      feudThreads: state.feudThreads,
      fatalities: state.fatalities,
      rookies: state.rookies,
    },
    roster: live.roster,
    teams: live.teams,
    ...(state.finance ? { finance: { events: state.finance.events } } : {}),
    complete: false,
  };
}

const EMPTY_STANDINGS: SeasonStandings = {
  throughRace: 0,
  racesCounted: [],
  drivers: [],
  byDriver: {},
  teams: [],
  byTeam: {},
};

/** One driver's afternoon, flattened out of a season race. */
export interface RaceLine {
  driverId: string;
  name: string;
  teamId: string | null;
  start: number | null;
  finish: number;
  running: boolean;
  outReason: string | null;
  turnsLed: number;
}

/** One race in the league's history, with enough to answer any record. */
export interface HistoryRace {
  seasonNumber: number;
  raceNumber: number;
  /** A running count across the whole league, 1-based. */
  leagueRaceIndex: number;
  raceKey: string;
  raceName: string;
  trackId: string;
  /** The physical track, the same across card years. */
  venue: string;
  trackName: string;
  winnerId: string | null;
  poleId: string | null;
  lines: RaceLine[];
  /** The most cars collected in a single turn — the race's biggest wreck. */
  biggestWreck: { turn: number; cars: number; driverIds: string[] } | null;
  /** True when a 'big one' was called (wreck.big-one). */
  bigOne: boolean;
}

function teamOfIn(season: HistorySeason, driverId: string): string | null {
  for (const t of season.teams) if (t.driverIds.includes(driverId)) return t.teamId;
  return season.roster.find((c) => c.driverId === driverId)?.teamId ?? null;
}

/** Flatten one season race. */
export function historyRace(season: HistorySeason, r: SeasonRaceResult, leagueRaceIndex: number): HistoryRace {
  const lines: RaceLine[] = r.race.finishingOrder.map((f) => ({
    driverId: f.driverId,
    name: f.name,
    teamId: teamOfIn(season, f.driverId),
    start: r.startPositions[f.driverId] ?? null,
    finish: f.place,
    running: f.status === 'running',
    outReason: f.outReason ?? null,
    turnsLed: f.turnsLed,
  }));
  let biggest: HistoryRace['biggestWreck'] = null;
  for (const t of r.race.turns) {
    if (t.collected.length > (biggest?.cars ?? 1)) {
      biggest = { turn: t.turn, cars: t.collected.length, driverIds: [...t.collected] };
    }
  }
  const pole = Object.entries(r.startPositions).find(([, p]) => p === 1)?.[0] ?? null;
  return {
    seasonNumber: season.seasonNumber,
    raceNumber: r.raceNumber,
    leagueRaceIndex,
    raceKey: r.scheduled.raceKey,
    raceName: r.scheduled.raceName,
    trackId: r.scheduled.trackId,
    venue: r.scheduled.venue ?? r.scheduled.trackId,
    trackName: r.scheduled.trackName,
    winnerId: r.race.winnerId,
    poleId: pole,
    lines,
    biggestWreck: biggest,
    bigOne: r.race.events.some((e) => e.momentTag === 'wreck.big-one'),
  };
}

/** Every race the league has run, oldest first. */
export function allRaces(seasons: readonly HistorySeason[]): HistoryRace[] {
  const out: HistoryRace[] = [];
  let i = 0;
  for (const s of [...seasons].sort((a, b) => a.seasonNumber - b.seasonNumber)) {
    for (const r of s.season.races) out.push(historyRace(s, r, ++i));
  }
  return out;
}

export function sortedSeasons(seasons: readonly HistorySeason[]): HistorySeason[] {
  return [...seasons].sort((a, b) => a.seasonNumber - b.seasonNumber);
}

/** Positive winnings + contingency, by driver, for one season's money. */
export function winningsBy(events: readonly MoneyEvent[] | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.account.kind !== 'driver' || e.amountMb <= 0) continue;
    if (e.category !== 'winnings' && e.category !== 'contingency') continue;
    out.set(e.account.id, (out.get(e.account.id) ?? 0) + e.amountMb);
  }
  return out;
}

/** Everything a driver's own book took in or paid out, by driver. */
export function earningsBy(events: readonly MoneyEvent[] | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.account.kind !== 'driver') continue;
    out.set(e.account.id, (out.get(e.account.id) ?? 0) + e.amountMb);
  }
  return out;
}
